// One value in the address's query, read and written the same way on every
// screen (release 3.3, Back model B5 and B7 of the 7 Oct plan).
//
// # PUSH OR REPLACE
//
// A tab and an open drawer are places a person goes to, so Back should step
// out of them: a tab is written with push, and a drawer pushes an entry of its
// own that closing it steps back out of (useDrawerParam). A filter, a sort, a search or a
// page is a way of looking at the same place, so Back should not step through
// every click: those are written with replace. Writing the value into the
// address at all is what makes a link or a reload open the same tab, drawer
// or filter (B7).
//
// # WHAT A PUSHED ENTRY KEEPS
//
// The new entry keeps where the record was opened from (history.state.from,
// lib/origin.ts), so the breadcrumb still says "My work > ..." on every tab.
// It does not copy the scroll key (history.state.k, lib/scrollPlace.ts): two
// entries sharing a key would share one remembered scroll position, and Back
// would land the earlier tab at the later one's place.
//
// pushState and replaceState raise no event, so the screen that writes a value
// sets its own state as well; Back and Forward raise hashchange, which the
// screen listens for (useHashParam).

import { useEffect, useState } from 'react'

function queryOf(hash: string): URLSearchParams {
  const i = hash.indexOf('?')
  return new URLSearchParams(i < 0 ? '' : hash.slice(i + 1))
}

export function hashParam(name: string): string | null {
  if (typeof window === 'undefined') return null
  return queryOf(window.location.hash).get(name)
}

// The address with one value set, or removed when value is null or empty.
export function withHashParam(hash: string, name: string, value: string | null): string {
  const raw = hash.replace(/^#/, '')
  const i = raw.indexOf('?')
  const path = i < 0 ? raw : raw.slice(0, i)
  const q = queryOf('#' + raw)
  if (value === null || value === '') q.delete(name)
  else q.set(name, value)
  const s = q.toString()
  return '#' + path + (s ? '?' + s : '')
}

export function setHashParam(name: string, value: string | null, how: 'push' | 'replace'): void {
  if (typeof window === 'undefined') return
  const next = withHashParam(window.location.hash, name, value)
  if (next === (window.location.hash || '#')) return
  try {
    const state = (history.state ?? {}) as Record<string, unknown>
    if (how === 'push') {
      const { k: _k, ...kept } = state
      history.pushState(kept, '', next)
    } else {
      history.replaceState(state, '', next)
    }
  } catch {
    // A browser that refuses history keeps the value for this visit only.
  }
}

// Whether the history entry on screen is the one a drawer of this name pushed
// when it opened, in this document. The mark carries a token of the page that
// wrote it: after a reload or a restored tab the entry below belongs to the
// page before, so stepping back to it would leave the page, and a drawer
// without its own entry here is closed by replacing instead.
const DOCUMENT_TOKEN = Math.random().toString(36).slice(2, 10)

export function ownsEntry(name: string): boolean {
  try {
    const s = (history.state ?? {}) as { drawer?: unknown; drawerDoc?: unknown }
    return s.drawer === name && s.drawerDoc === DOCUMENT_TOKEN
  } catch {
    return false
  }
}

// The entry a drawer pushes when it opens: the address with its value, marked
// as the drawer's own, keeping the visit's origin and leaving the scroll key
// behind with the entry below. Opening the row already on screen adds nothing.
export function pushDrawerEntry(name: string, value: string): void {
  if (typeof window === 'undefined') return
  const next = withHashParam(window.location.hash, name, value)
  if (next === window.location.hash) return
  try {
    const { k: _k, ...kept } = (history.state ?? {}) as Record<string, unknown>
    history.pushState({ ...kept, drawer: name, drawerDoc: DOCUMENT_TOKEN }, '', next)
  } catch {
    // Without history the drawer still opens, for this visit.
  }
}

// A close already under way, so a second press (a double click, Escape and
// then Close) waits for the first instead of stepping back a second time and
// leaving the page.
let closing: Promise<void> | null = null

// A drawer kept in the address (B7, B10). Opening it pushes an entry marked as
// the drawer's own, so Back closes it. Closing it from the page (Escape, the
// close button, a finished action) steps back out of that entry rather than
// pushing a new one, so a later Back does not open it again; a drawer reached
// by a link, with no entry of its own to step out of, is closed by replacing
// the address without it. Moving to another row inside an open drawer
// (Previous, Next) replaces, so Back leaves the drawer instead of walking the
// rows.
export function useDrawerParam(name: string): {
  value: string | null
  open: (v: string) => void
  move: (v: string) => void
  // Resolves once the address no longer names the drawer, so whatever the
  // page does next (open another dialog) happens after focus has gone back.
  close: () => Promise<void>
} {
  const [value, setValue] = useHashParam(name, 'replace')
  return {
    value,
    open: (v) => {
      pushDrawerEntry(name, v)
      setValue(v)
    },
    move: (v) => setValue(v),
    close: () => {
      if (closing) return closing
      closing = new Promise<void>((done) => {
        const finish = () => {
          window.clearTimeout(guard)
          window.removeEventListener('popstate', onPop)
          closing = null
          window.setTimeout(done, 0)
        }
        const onPop = () => finish()
        // A step back that raises no popstate in this page within two
        // seconds (the browser would have left the page) is not waited for.
        const guard = window.setTimeout(finish, 2000)
        if (ownsEntry(name)) {
          window.addEventListener('popstate', onPop)
          history.back()
          return
        }
        setValue(null)
        finish()
      })
      return closing
    },
  }
}

// A value kept in the address: the current value, and a setter that writes it
// (push or replace) and updates the screen at once. Back and Forward read it
// again from the address.
export function useHashParam(name: string, how: 'push' | 'replace'): [string | null, (v: string | null) => void] {
  const [value, setValue] = useState<string | null>(() => hashParam(name))
  useEffect(() => {
    const sync = () => setValue(hashParam(name))
    window.addEventListener('hashchange', sync)
    window.addEventListener('popstate', sync)
    return () => {
      window.removeEventListener('hashchange', sync)
      window.removeEventListener('popstate', sync)
    }
  }, [name])
  const set = (v: string | null) => {
    setHashParam(name, v, how)
    setValue(v === '' ? null : v)
  }
  return [value, set]
}
